import {Course, IltSession, TinyCourse, User} from "@lib/talentlms/talentlms-model";
import {groupBy} from "@lib/utils/array-utils";
import {addMinutes, newDate} from "@lib/utils/date-utils";

export type CourseWithSessions = {
    course: Course,
    iltSessions: IltSession[],
}

const timeZoneKeys = ['Prague', 'Chicago'] as const
type TimeZoneKey = (typeof timeZoneKeys)[number]
export type TimeZone = 'America/Chicago' | 'Europe/Prague'
export type Locale = 'en-US' | 'en-UK'
export const defaultTimeZone: TimeZone = 'Europe/Prague'

const timeZoneKeyToTimeZone: Record<TimeZoneKey, TimeZone> = {
    Prague: 'Europe/Prague',
    Chicago: 'America/Chicago',
}

const timeZoneKeyToLocale: Record<TimeZoneKey, Locale> = {
    Prague: "en-UK",
    Chicago: "en-US",
}

export const parseTalentLmsDate = (talentLmsDate: string, timeZone: TimeZone): Date => {
    const pattern = /(?<day>[0-9]{2})\/(?<month>[0-9]{2})\/(?<year>[0-9]{4}), (?<hour>[0-9]{2}):(?<minute>[0-9]{2}):(?<second>[0-9]{2})/
    type Group = { day: string, month: string, year: string, hour: string, minute: string, second: string }
    const {day, month, year, hour, minute, second} = pattern.exec(talentLmsDate)?.groups as Group
    return newDate(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second), timeZone)
}

export const sortSessions = (sessions: IltSession[]) => (
    sessions.sort((s1, s2) => parseInt(s1.start_timestamp) - parseInt(s2.start_timestamp))
)

/**
 * The time zone must be embedded in the session name
 * @param session
 */
export const getTimeZoneKey = (session: IltSession): TimeZoneKey => {
    for (const key of timeZoneKeys) {
        const pattern = new RegExp(key, 'i')
        const sessionName = session.multiname === '' ? session.name : session.multiname
        if (pattern.exec(sessionName)) {
            return key
        }
    }
    throw new Error(`Failed to determine time zone for ILT session. The session name must contain one of the following values: ${JSON.stringify(timeZoneKeys)}`)
}

export const getTimeZone = (session: IltSession): TimeZone => timeZoneKeyToTimeZone[getTimeZoneKey(session)];

export const getLocale = (session: IltSession): Locale => timeZoneKeyToLocale[getTimeZoneKey(session)]

export type CourseDelivery = 'IltCourse' | 'CodingExam' | 'LmsExam' | 'OnDemandCourse' | string

export const getCourseDelivery = (course: TinyCourse): CourseDelivery | undefined => {
    return course.custom_field_5
}

export const isAvailable = (course: TinyCourse) => {
    const isInactive = course.status !== 'active'
    const isHiddenFromCatalog = course.hide_from_catalog !== '0'
    return !isHiddenFromCatalog && !isInactive
}

export const isMultiSession = (session: IltSession) => session.multiname !== ""

export const groupSessions = (sessions: IltSession[]): IltSession[][] => {
    const multiSessions = sessions.filter(s => isMultiSession(s))
    const singleSessions = sessions.filter(s => !isMultiSession(s))
    const groupedMultiSessions = groupBy(multiSessions, 'multiname')
    return [
        ...Object.values(groupedMultiSessions),
        ...singleSessions.map(s => [s]),
    ]
}

export const formatDate = (date: Date, locale: Locale) => {
    const dateFormatter = new Intl.DateTimeFormat(locale, {dateStyle: 'long'})
    return dateFormatter.format(date)
}

export const formatTime = (date: Date, locale: Locale) => {
    const dateFormatter = new Intl.DateTimeFormat(locale, {timeStyle: 'short'})
    return dateFormatter.format(date)
}

export const getStartDateTime = (iltSession: IltSession) => (
    parseTalentLmsDate(iltSession.start_date, getTimeZone(iltSession))
)

export const getEndDateTime = (iltSession: IltSession) => (
    addMinutes(parseTalentLmsDate(iltSession.start_date, getTimeZone(iltSession)), parseInt(iltSession.duration_minutes))
)

export const getFormattedDuration = (session: IltSession) => {
    const durationMinutes = parseInt(session.duration_minutes)
    const hours = Math.floor(durationMinutes / 60)
    const minutes = durationMinutes % 60
    return minutes !== 0 ? `${hours} hours ${minutes} minutes` : `${hours} hours`
}

const getUrlId = (url: string): string | undefined => (
    /id:(?<id>[0-9]*)/.exec(url)?.groups?.id
)

export const getCourseIdFromCourseUrl = (courseUrl: string): string | undefined => (
    getUrlId(courseUrl)
)

export const getIltUnitIdFromIltUrl = (iltUrl: string): string | undefined => (
    getUrlId(iltUrl)
)

export const getFullName = (user: User): string => `${user.first_name} ${user.last_name}`